import React, { useCallback, useEffect, useState } from 'react';
import {
  Row, Col, Card, Statistic, Typography, Button, Skeleton, Tabs, Segmented, Space,
  Tag, Empty, Modal, Form, Input, InputNumber, DatePicker, Upload, Popconfirm, App, Alert, Badge,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, UploadOutlined, DeleteOutlined,
  FilePdfOutlined, FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd';
import { adminApi } from '../services/api';
import { Elenco, Voce } from '../components/Elenco';

const { Title, Text } = Typography;

interface ActiveSub {
  id: number; name: string; email: string; business_name: string | null;
  plan: string; status: string; amount_net: number; iva: number;
  amount_gross: number; renews_at: string | null;
}
interface AccountingData {
  period: { from: string; to: string };
  incassi_lordi: number; iva: number; incassi_netti: number;
  spese_meta: number; ricavi_netti: number; active_subs: ActiveSub[];
}
interface ManualInvoice {
  id: number; number: string | null; customer_name: string; customer_email: string | null;
  invoice_date: string; amount_net: number; amount_tax: number; amount_gross: number;
  notes: string | null; pdf_url: string | null; xml_url: string | null;
}

type Periodo = 'month' | 'last_month' | 'quarter' | 'year';

const PERIODI: { value: Periodo; label: string }[] = [
  { value: 'month',      label: 'Mese corrente' },
  { value: 'last_month', label: 'Mese scorso' },
  { value: 'quarter',    label: 'Trimestre' },
  { value: 'year',       label: 'Anno' },
];

function intervallo(p: Periodo) {
  const n = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  const f = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (p === 'last_month')
    return { from: f(new Date(n.getFullYear(), n.getMonth() - 1, 1)), to: f(new Date(n.getFullYear(), n.getMonth(), 0)) };
  if (p === 'quarter') {
    const q = Math.floor(n.getMonth() / 3);
    return { from: f(new Date(n.getFullYear(), q * 3, 1)), to: f(new Date(n.getFullYear(), q * 3 + 3, 0)) };
  }
  if (p === 'year') return { from: `${n.getFullYear()}-01-01`, to: `${n.getFullYear()}-12-31` };
  return { from: f(new Date(n.getFullYear(), n.getMonth(), 1)), to: f(new Date(n.getFullYear(), n.getMonth() + 1, 0)) };
}

const PIANO: Record<string, { t: string; c: string }> = {
  trial: { t: 'Trial', c: 'purple' }, base: { t: 'Base', c: 'default' },
  medium: { t: 'Medium', c: 'blue' }, premium: { t: 'Premium', c: 'green' },
};

const euro = (n: number) =>
  '€ ' + Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const gg = (d?: string | null) => {
  if (!d) return '—';
  const t = new Date(d);
  return isNaN(+t) ? String(d) : t.toLocaleDateString('it-IT');
};

export default function AccountingPage() {
  const { message } = App.useApp();
  const [periodo, setPeriodo] = useState<Periodo>('month');
  const [data, setData] = useState<AccountingData | null>(null);
  const [invoices, setInvoices] = useState<ManualInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const [modale, setModale] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [pdf, setPdf] = useState<UploadFile[]>([]);
  const [xml, setXml] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    const { from, to } = intervallo(periodo);
    setLoading(true);
    setErrore('');
    try {
      const [a, b] = await Promise.all([
        adminApi.accounting(from, to),
        adminApi.adminInvoices(from, to),
      ]);
      setData(a.data);
      setInvoices(Array.isArray(b.data) ? b.data : []);
    } catch {
      setErrore('Non è stato possibile caricare i dati contabili.');
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  const salva = async (v: any) => {
    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append('customer_name', v.customer_name);
      fd.append('invoice_date', v.invoice_date.format('YYYY-MM-DD'));
      fd.append('amount_net', String(v.amount_net));
      if (v.number) fd.append('number', v.number);
      if (v.customer_email) fd.append('customer_email', v.customer_email);
      if (v.notes) fd.append('notes', v.notes);
      if (pdf[0]?.originFileObj) fd.append('pdf', pdf[0].originFileObj as Blob);
      if (xml[0]?.originFileObj) fd.append('xml', xml[0].originFileObj as Blob);

      await adminApi.createAdminInvoice(fd);
      message.success('Fattura salvata');
      setModale(false);
      form.resetFields();
      setPdf([]); setXml([]);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Salvataggio non riuscito. Controlla i dati e riprova.');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async (id: number) => {
    try {
      await adminApi.deleteAdminInvoice(id);
      setInvoices(p => p.filter(i => i.id !== id));
      message.success('Fattura eliminata');
    } catch {
      message.error('Eliminazione non riuscita');
    }
  };

  const colFat: ColumnsType<ManualInvoice> = [
    { title: 'Numero', dataIndex: 'number', width: 110,
      render: v => v || <Text type="secondary">—</Text> },
    { title: 'Data', dataIndex: 'invoice_date', width: 115, render: gg,
      sorter: (a, b) => +new Date(a.invoice_date) - +new Date(b.invoice_date), defaultSortOrder: 'descend' },
    { title: 'Cliente', dataIndex: 'customer_name',
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600, color: '#101527' }}>{r.customer_name}</div>
          {r.customer_email && <Text type="secondary" style={{ fontSize: 12.5 }}>{r.customer_email}</Text>}
        </>
      ) },
    { title: 'Imponibile', dataIndex: 'amount_net', width: 120, align: 'right', render: euro },
    { title: 'IVA', dataIndex: 'amount_tax', width: 105, align: 'right', responsive: ['lg'], render: euro },
    { title: 'Totale', dataIndex: 'amount_gross', width: 120, align: 'right',
      render: v => <Text strong>{euro(v)}</Text> },
    {
      title: '', width: 120, align: 'right',
      render: (_, r) => (
        <Space size={2}>
          {r.pdf_url && <Button type="text" size="small" icon={<FilePdfOutlined />}
                                href={r.pdf_url} target="_blank" title="PDF" />}
          {r.xml_url && <Button type="text" size="small" icon={<FileTextOutlined />}
                                href={r.xml_url} target="_blank" title="XML" />}
          {/* Conferma in linea al posto di window.confirm: si legge, e si annulla */}
          <Popconfirm
            title="Eliminare questa fattura?"
            description="L'operazione non si può annullare."
            okText="Elimina" cancelText="Annulla" okButtonProps={{ danger: true }}
            onConfirm={() => elimina(r.id)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Elimina" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const colSub: ColumnsType<ActiveSub> = [
    { title: 'Cliente', dataIndex: 'name',
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600, color: '#101527' }}>{r.name}</div>
          <Text type="secondary" style={{ fontSize: 12.5 }}>{r.business_name || r.email}</Text>
        </>
      ) },
    { title: 'Piano', dataIndex: 'plan', width: 110,
      render: p => PIANO[p] ? <Tag color={PIANO[p].c}>{PIANO[p].t}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Imponibile', dataIndex: 'amount_net', width: 120, align: 'right', render: euro },
    { title: 'IVA', dataIndex: 'iva', width: 105, align: 'right', responsive: ['lg'], render: euro },
    { title: 'Totale', dataIndex: 'amount_gross', width: 120, align: 'right',
      render: v => <Text strong>{euro(v)}</Text> },
    { title: 'Rinnovo', dataIndex: 'renews_at', width: 115, responsive: ['lg'], render: gg },
  ];

  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} style={{ maxWidth: 340, marginBottom: 22 }} />
        <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
          {[0, 1, 2, 3, 4].map(i => (
            <Col xs={12} xl={5} key={i}><Card><Skeleton active paragraph={{ rows: 1 }} /></Card></Col>
          ))}
        </Row>
        <Card><Skeleton active paragraph={{ rows: 7 }} /></Card>
      </>
    );
  }

  const utile = data?.ricavi_netti ?? 0;

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Contabilità</Title>
          <Text type="secondary">
            {data?.period ? `Dal ${gg(data.period.from)} al ${gg(data.period.to)}` : 'Incassi, spese e IVA'}
          </Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={load}>Aggiorna</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModale(true)}>
            Nuova fattura
          </Button>
        </Space>
      </div>

      {errore && (
        <Alert type="error" showIcon message={errore} style={{ marginBottom: 16 }}
               action={<Button size="small" onClick={load}>Riprova</Button>} />
      )}

      <Segmented
        options={PERIODI}
        value={periodo}
        onChange={v => setPeriodo(v as Periodo)}
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
        <Col xs={12} xl={5}>
          <Card><Statistic title="Incassi lordi" value={data?.incassi_lordi ?? 0}
                           formatter={v => euro(Number(v))} /></Card>
        </Col>
        <Col xs={12} xl={5}>
          <Card><Statistic title="di cui IVA" value={data?.iva ?? 0}
                           formatter={v => euro(Number(v))} /></Card>
        </Col>
        <Col xs={12} xl={5}>
          <Card><Statistic title="Incassi netti" value={data?.incassi_netti ?? 0}
                           formatter={v => euro(Number(v))} /></Card>
        </Col>
        <Col xs={12} xl={4}>
          <Card><Statistic title="Spesa pubblicitaria" value={data?.spese_meta ?? 0}
                           formatter={v => euro(Number(v))}
                           valueStyle={{ color: '#DC3055' }} /></Card>
        </Col>
        <Col xs={24} xl={5}>
          <Card>
            <Statistic title="Margine" value={utile} formatter={v => euro(Number(v))}
                       valueStyle={{ color: utile >= 0 ? '#0EA26B' : '#DC3055' }} />
          </Card>
        </Col>
      </Row>

      <Card styles={{ body: { padding: 0 } }}>
        <Tabs
          defaultActiveKey="fat"
          tabBarStyle={{ padding: '0 20px', marginBottom: 0 }}
          items={[
            {
              key: 'fat',
              label: <>Fatture <Badge count={invoices.length} showZero color="#E4E7F0"
                        style={{ color: '#3B4460' }} /></>,
              children: (
                <Elenco<ManualInvoice>
                  righe={invoices} colonne={colFat} rowKey={r => r.id}
                  vuoto={
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                           description="Nessuna fattura in questo periodo">
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => setModale(true)}>
                        Aggiungi la prima
                      </Button>
                    </Empty>
                  }
                  scheda={r => (
                    <>
                      <div className="scheda-titolo">{r.customer_name}</div>
                      <div className="scheda-sub">{r.number ? `N. ${r.number} · ` : ''}{gg(r.invoice_date)}</div>
                      <Voce etichetta="Imponibile">{euro(r.amount_net)}</Voce>
                      <Voce etichetta="IVA">{euro(r.amount_tax)}</Voce>
                      <Voce etichetta="Totale"><strong>{euro(r.amount_gross)}</strong></Voce>
                    </>
                  )}
                />
              ),
            },
            {
              key: 'sub',
              label: <>Abbonamenti attivi <Badge count={data?.active_subs?.length ?? 0} showZero
                        color="#E4E7F0" style={{ color: '#3B4460' }} /></>,
              children: (
                <Elenco<ActiveSub>
                  righe={data?.active_subs ?? []} colonne={colSub} rowKey={r => r.id}
                  vuoto={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Nessun abbonamento attivo in questo periodo" />}
                  scheda={r => (
                    <>
                      <div className="scheda-titolo">{r.name}</div>
                      <div className="scheda-sub">{r.business_name || r.email}</div>
                      <Voce etichetta="Piano">{PIANO[r.plan] ? <Tag color={PIANO[r.plan].c}>{PIANO[r.plan].t}</Tag> : '—'}</Voce>
                      <Voce etichetta="Imponibile">{euro(r.amount_net)}</Voce>
                      <Voce etichetta="Totale"><strong>{euro(r.amount_gross)}</strong></Voce>
                    </>
                  )}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* Il form vive in una modale: prima era un blocco che compariva in mezzo
          alla pagina e spostava tutto il resto verso il basso. */}
      <Modal
        title="Nuova fattura"
        open={modale}
        onCancel={() => !salvando && setModale(false)}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={salvando}
        onOk={() => form.submit()}
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={salva} requiredMark={false}
              initialValues={{ invoice_date: undefined }}>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item name="number" label="Numero">
                <Input placeholder="es. 2026/014" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="invoice_date" label="Data"
                         rules={[{ required: true, message: 'Indica la data' }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="customer_name" label="Cliente"
                     rules={[{ required: true, message: 'Indica il cliente' }]}>
            <Input placeholder="Ragione sociale o nome" />
          </Form.Item>

          <Form.Item name="customer_email" label="Email"
                     rules={[{ type: 'email', message: 'Indirizzo non valido' }]}>
            <Input placeholder="facoltativa" />
          </Form.Item>

          <Form.Item name="amount_net" label="Imponibile"
                     rules={[{ required: true, message: "Indica l'imponibile" }]}
                     extra="L'IVA al 22% viene calcolata dal sistema.">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2}
                         addonBefore="€" placeholder="0,00" />
          </Form.Item>

          <Form.Item name="notes" label="Note">
            <Input.TextArea rows={2} placeholder="facoltative" />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="PDF">
                <Upload fileList={pdf} onChange={({ fileList }) => setPdf(fileList.slice(-1))}
                        beforeUpload={() => false} maxCount={1} accept=".pdf">
                  <Button icon={<UploadOutlined />} block>Allega PDF</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="XML">
                <Upload fileList={xml} onChange={({ fileList }) => setXml(fileList.slice(-1))}
                        beforeUpload={() => false} maxCount={1} accept=".xml">
                  <Button icon={<UploadOutlined />} block>Allega XML</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
